import { createElement } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { type ContextualNavSection, type ContextualNavEntry } from '@vnetwork/ui/layout';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { useServiceCdnAccess } from '@/api/manticore/service-cdn';
import { CDN_NAV, cdnHref, cdnActiveId } from './cdn-console-nav';
import ManticoreOverview from '../ManticoreOverview';
import { DomainList } from '../DomainList';
import { CdnSettingsPage } from './CdnSettingsPage';
import { CdnAnalyticsPage } from './CdnAnalyticsPage';

// Slugs a report-only member may reach — overview/analytics/usage only. Business
// Usage now links out to the org-wide /services/cdn/usage page (filterable by
// service). Mirrors the BE allowlist in modules/service/cdn/access/middleware.go;
// the BE still enforces.
const REPORT_SLUGS = new Set(['', 'analytics', 'usage']);

/**
 * Service-scoped CDN orchestrator console. The per-service navigation lives in
 * the app's main ContextualSidebar (registered via useSetContextualNav) — entering
 * the console swaps the whole sidebar to its scope with a "Back to Services" link
 * (straight to the services dashboard, not the CDN list — one hop out).
 * Each sub-page supplies its own page chrome (Container + PageHeader).
 */
export function CdnConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/cdn/${serviceId}`;

  // Report-only members see a read-only console (overview/analytics/usage). The BE
  // 403s management routes regardless — this just hides them in the UI.
  const { data: accessMode } = useServiceCdnAccess(serviceId ?? '');
  const report = accessMode === 'report';

  useSetContextualNav(() => {
    const entries = report ? CDN_NAV.filter((e) => REPORT_SLUGS.has(e.slug)) : CDN_NAV;
    const items: ContextualNavEntry[] = entries.map((entry) => {
      const icon = createElement(entry.icon, { size: 16 });
      if (entry.children) {
        // Collapsible parent — children are the navigable leaves.
        return {
          id: cdnHref(base, entry.slug),
          label: entry.label,
          icon,
          items: entry.children.map((child) => {
            const href = cdnHref(base, child.slug);
            return { id: href, label: child.label, onSelect: () => navigate(href) };
          }),
        };
      }
      // `href` override leaves the console (e.g. Business Usage → org-wide page).
      const href = entry.href ?? cdnHref(base, entry.slug);
      return { id: href, label: entry.label, icon, onSelect: () => navigate(href) };
    });
    const sections: ContextualNavSection[] = [{ items }];
    return {
      sections,
      activeId: cdnActiveId(base, pathname),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate, report]);

  // Report-only members are redirected away from management routes (domains/settings).
  const managed = (el: JSX.Element) => (report ? <Navigate to={base} replace /> : el);

  return (
    <Routes>
      <Route index element={<ManticoreOverview />} />
      <Route path="overview" element={<ManticoreOverview />} />
      <Route path="domains" element={managed(<DomainList />)} />
      <Route path="analytics" element={<CdnAnalyticsPage view="monitoring" />} />
      <Route path="analytics/monitoring" element={<CdnAnalyticsPage view="monitoring" />} />
      <Route path="analytics/overview" element={<CdnAnalyticsPage view="overview" />} />
      <Route path="analytics/location" element={<CdnAnalyticsPage view="location" />} />
      <Route path="settings" element={managed(<CdnSettingsPage />)} />
      <Route path="*" element={<ManticoreOverview />} />
    </Routes>
  );
}
